/**
 * Pure math and bookkeeping for video clips placed as layers. Shared by the
 * export frame sources, live editing playback, the audio mixer and the clip
 * properties panel, so all of them agree on which source frame shows when.
 *
 * Timing model: a clip layer plays from the start of its scene. Its trimmed
 * range [trimStartMs, trimEndMs) runs at `rate`; past the end it holds the
 * last frame, or wraps when `loop` is set.
 *
 * DOM-free and Fabric-free so it unit-tests in node.
 */

import type { FabricCanvasData, ProjectPage, VideoClipRef, VideoProject } from '@canvas/shared-types';
import { buildCues } from './sceneTimeline';

/** Fabric `type` of a video layer. */
export const VIDEO_OBJECT_TYPE = 'craftaraVideo';

/** Assumed when a clip's frame rate couldn't be measured at import. */
export const DEFAULT_SOURCE_FPS = 30;
/** Shortest trimmed range a clip may have. */
export const MIN_CLIP_MS = 100;
export const MIN_CLIP_RATE = 0.25;
export const MAX_CLIP_RATE = 4;
export const DEFAULT_CLIP_VOLUME = 1;
/**
 * Stay this far inside the source end: seeking to exactly `duration` shows a
 * black frame in Chrome and never fires `seeked` in some Safari builds.
 */
const END_GUARD_MS = 1;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function createClipRef(args: {
  clipId: string;
  source: VideoClipRef['source'];
  mimeType: string;
  naturalWidth: number;
  naturalHeight: number;
  durationMs: number;
  fps?: number;
  idbKey?: string;
  storagePath?: string;
  url?: string;
  posterIdbKey?: string;
  posterStoragePath?: string;
}): VideoClipRef {
  return clampClip({
    v: 1,
    ...args,
    trimStartMs: 0,
    trimEndMs: args.durationMs,
    rate: 1,
    loop: false,
    muted: false,
    volume: DEFAULT_CLIP_VOLUME,
  });
}

/** Keep every field inside its legal range. */
export function clampClip(clip: VideoClipRef): VideoClipRef {
  const duration = Math.max(MIN_CLIP_MS, Math.round(clip.durationMs) || MIN_CLIP_MS);
  const trimStartMs = Math.round(clamp(clip.trimStartMs || 0, 0, duration - MIN_CLIP_MS));
  const trimEndMs = Math.round(
    clamp(clip.trimEndMs || duration, trimStartMs + MIN_CLIP_MS, duration)
  );
  const fps = clip.fps && Number.isFinite(clip.fps) ? clamp(clip.fps, 1, 240) : undefined;
  const next: VideoClipRef = {
    ...clip,
    durationMs: duration,
    trimStartMs,
    trimEndMs,
    rate: clamp(Number.isFinite(clip.rate) && clip.rate > 0 ? clip.rate : 1, MIN_CLIP_RATE, MAX_CLIP_RATE),
    loop: Boolean(clip.loop),
    muted: Boolean(clip.muted),
    volume: clamp(Number.isFinite(clip.volume) ? clip.volume : DEFAULT_CLIP_VOLUME, 0, 1),
  };
  if (fps === undefined) delete next.fps;
  else next.fps = fps;
  return next;
}

/** Trimmed source length in ms. */
export function clipLengthMs(clip: VideoClipRef): number {
  return Math.max(0, clip.trimEndMs - clip.trimStartMs);
}

/** Timeline ms one full pass of the trimmed range takes at `rate`. */
export function clipPlayMs(clip: VideoClipRef): number {
  return clipLengthMs(clip) / clip.rate;
}

/**
 * Source ms to show at scene-local `sceneLocalMs`:
 * trimStart + (local - start) * rate, wrapped when looping, otherwise held
 * on the last frame.
 */
export function clipSourceMsAt(clip: VideoClipRef, sceneLocalMs: number, clipStartInSceneMs = 0): number {
  const length = clipLengthMs(clip);
  const lastMs = Math.max(clip.trimStartMs, clip.trimEndMs - END_GUARD_MS);
  if (length <= 0) return clip.trimStartMs;
  const elapsed = Math.max(0, sceneLocalMs - clipStartInSceneMs) * clip.rate;
  if (clip.loop) return Math.min(lastMs, clip.trimStartMs + (elapsed % length));
  return Math.min(lastMs, clip.trimStartMs + elapsed);
}

/** Index of the source frame that contains `srcMs`. */
export function sourceFrameIndex(srcMs: number, fps: number): number {
  // The epsilon keeps 1000/3-style timestamps from landing a frame early.
  return Math.floor((srcMs * fps) / 1000 + 1e-6);
}

/**
 * True when showing `nextSrcMs` needs a real seek. Two output frames that
 * fall inside the same source frame (a 24 fps source at 30 fps out does this
 * about 20% of the time) reuse the frame already painted — seeks dominate
 * export time for video-bearing scenes.
 */
export function needsSeek(lastSrcMs: number | null, nextSrcMs: number, fps: number): boolean {
  if (lastSrcMs === null) return true;
  return sourceFrameIndex(lastSrcMs, fps) !== sourceFrameIndex(nextSrcMs, fps);
}

/** True when the clip contributes sound. */
export function clipAudible(clip: VideoClipRef): boolean {
  return !clip.muted && clip.volume > 0;
}

/**
 * The poster `src` written into canvasData. Only a durable http(s) URL is
 * allowed: a blob: URL dies with the tab and a data: URL would put media
 * bytes into the Firestore document (1 MB limit). Anything else becomes ''
 * and the poster is re-resolved from posterIdbKey / posterStoragePath.
 */
export function serializablePosterSrc(candidate: string | null | undefined): string {
  return typeof candidate === 'string' && /^https?:\/\//i.test(candidate) ? candidate : '';
}

/** Strip anything in a clip ref that could carry an inline media payload. */
export function serializableClipRef(clip: VideoClipRef): VideoClipRef {
  const next = { ...clip };
  if (next.url && !serializablePosterSrc(next.url)) delete next.url;
  return next;
}

type JsonObject = Record<string, unknown>;

function readClipRef(obj: JsonObject): VideoClipRef | null {
  if (obj.type !== VIDEO_OBJECT_TYPE) return null;
  const ref = obj.craftaraVideo as VideoClipRef | undefined;
  return ref && typeof ref === 'object' && typeof ref.clipId === 'string' ? ref : null;
}

/** Every clip ref in a page's stored objects, including inside groups. */
export function clipRefsInCanvasData(data: FabricCanvasData | null | undefined): VideoClipRef[] {
  const out: VideoClipRef[] = [];
  const walk = (objects: unknown): void => {
    if (!Array.isArray(objects)) return;
    for (const raw of objects) {
      if (!raw || typeof raw !== 'object') continue;
      const obj = raw as JsonObject;
      const ref = readClipRef(obj);
      if (ref) out.push(ref);
      walk(obj.objects);
    }
  };
  walk(data?.objects);
  return out;
}

export interface PlacedClip {
  clip: VideoClipRef;
  /** Timeline ms the clip's scene starts. */
  startMs: number;
  /** Timeline ms the clip's scene ends. */
  endMs: number;
}

/** Every clip on the timeline with its scene's window, for the export audio mix. */
export function timelineClips(pages: ProjectPage[], video: VideoProject): PlacedClip[] {
  const byId = new Map(pages.map((p) => [p.pageId, p]));
  const out: PlacedClip[] = [];
  for (const cue of buildCues(video.scenes)) {
    for (const clip of clipRefsInCanvasData(byId.get(cue.pageId)?.canvasData)) {
      out.push({ clip, startMs: cue.startMs, endMs: cue.endMs });
    }
  }
  return out;
}

/** True when any page holds a clip that only exists in this browser. */
export function hasUnbackedClips(pages: ProjectPage[]): boolean {
  return pages.some((p) => clipRefsInCanvasData(p.canvasData).some((c) => c.source === 'idb'));
}
